// @ts-nocheck
import { forwardRef } from "@chakra-ui/react";
import type {
    FC,
    ReactChild,
    ReactElement,
    ReactFragment,
    ReactNode,
    ReactPortal,
    Ref,
} from "react";
import { useEffect, useMemo, Children, isValidElement, useRef, useState, memo } from "react";
import { H } from "../../content/heading/A11yHeading";
import { IconButton } from "../../forms/icon-button/IconButton";
import type { BoxProps } from "../../layout/box/Box";
import { Box } from "../../layout/box/Box";
import type { FlexProps } from "../../layout/flex/Flex";
import { Flex } from "../../layout/flex/Flex";
import { styles } from "./CarouselStyles";
import { useIsomorphicLayoutEffect } from "../../../hooks/useIsomorphicLayoutEffect";
import { useNavigationControls } from "./hooks/useNavigationControls";
import { useSliderNodeHandler } from "./hooks/useSliderNodeHandler";
import { useSlidesPerView } from "./hooks/useSlidesPerView";
import { ChevronLeftIcon, ChevronRightIcon, PauseIcon, PlayIcon } from "./Icons";

/* Swiper Carousel imports only for Hero Carousel*/
import { A11y, Autoplay, Keyboard, Navigation, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/navigation";
import "swiper/css/pagination";
import type { SwiperProps } from "swiper/react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper/types";
import { sizes } from "../../../design-tokens";
import { CustomProgressBar } from "./Progress";

export type CarouselProps = BoxProps & {
    extraNavigationButtons?: ReactNode;
    showNavigation?: boolean;
    titleAriaLabel?: string;
    slidesPerView?: number | number[];
    gap?: number;
    justifySlider?: string | string[];
    timer?: boolean;
    delay?: number;
    newActiveSlide?: number;
    navsx?: {
        rightButton?: object;
        leftButton?: object;
        flexContainer?: object;
    };
    theme?: string;
};
export type CarouselSliderProps = FlexProps;
export type CarouselItemProps = FlexProps & {
    itemWidth: number;
    ref?: Ref<HTMLDivElement>;
    index?: number;
    setActiveSlide?: (index: number) => void;
    setActiveTheme?: (theme: string) => void;
};
export type CarouselNavigationProps = FlexProps &
    SwiperProps & {
        arrayChildren: (ReactChild | ReactFragment | ReactPortal)[];
        sliderNode: HTMLDivElement;
        itemWidth: number;
        nextSlideTitle?: string;
        previousSlideTitle?: string;
        navsx?: {
            rightButton?: object;
            leftButton?: object;
            flexContainer?: object;
        };
    };
// We need to move the hook one level up, so the pagination and the navigation are linked.
export type CarouselHeroProps = FlexProps & {
    children: ReactElement | ReactElement[];
    extraNavigationButtons?: ReactNode;
    timer?: boolean;
    carouselIndicators?: boolean;
    iconSize?: object;
    containerStyle?: object;
    selectedImageIndex?: number;
    previousSlideTitle?: string;
    nextSlideTitle?: string;
    customNavigation?: ReactElement;
    navsx?: {
        rightButton?: object;
        leftButton?: object;
        flexContainer?: object;
    };
    paginationsx?: object;
    hasPagination?: boolean;
    setImageIndex?: (index: number) => void;
    playPauseControls?: boolean;
    setIsVideoPlaying?: (isPlaying: boolean) => void;
    activeSlide?: number;
    isVideoPlaying?: boolean;
    setActiveSlide?: (activeSlide: number) => void;
    instanceId?: string;
    loop?: boolean;
};
export type CarouselProductImageNavigationProps = Omit<CarouselHeroProps, "children"> & {
    onClick: () => void;
    activeTheme?: string;
    navsx?: {
        rightButton?: object;
        leftButton?: object;
        flexContainer?: object;
    };
    swiperRef?: Ref<SwiperClass>;
    playPauseTitle?: string;
    playPauseControls?: boolean;
    uniquePrevClass?: string;
    uniqueNextClass?: string;
};

export type CarouselProductImagesPaginationProps = {
    slides: any[];
    activeIndex: number;
    paginationsx?: object;
};

const DEFAULT_GAP = 8;
const DEFAULT_TITLE = "";
export const DEFAULT_SLIDES_PER_VIEW = 1.5;
const DEFAULT_SHOW_NAVIGATION = false;
const DEFAULT_TITLE_ARIA_LABEL = "Carousel Title";
const DEFAULT_EXTRA_NAVIGATION_BUTTONS = null;
const DEFAULT_JUSTIFY_SLIDER = "flex-start";
const DEFAULT_TIMER = false;
const DEFAULT_ICON_SIZE = {
    height: `${sizes.icon["4xs"]} !important`,
    width: `${sizes.icon["4xs"]} !important`,
};

export const Carousel: FC<CarouselProps> = ({
    h,
    sx,
    navsx,
    gap = DEFAULT_GAP,
    title = DEFAULT_TITLE,
    height,
    children,
    showNavigation = DEFAULT_SHOW_NAVIGATION,
    titleAriaLabel = DEFAULT_TITLE_ARIA_LABEL,
    slidesPerView: _slidesPerView = DEFAULT_SLIDES_PER_VIEW,
    extraNavigationButtons = DEFAULT_EXTRA_NAVIGATION_BUTTONS,
    justifySlider = DEFAULT_JUSTIFY_SLIDER,
    newActiveSlide,
    theme,
    ...rest
}): JSX.Element => {
    const { slidesPerView } = useSlidesPerView(_slidesPerView);
    const { sliderRef, itemWidth, sliderNode } = useSliderNodeHandler<HTMLDivElement>(
        slidesPerView,
        gap,
        Children.toArray(children) as ReactElement[]
    );

    const themeSurface = useMemo(() => styles.theme[theme]?.surface, [theme]);
    const themeText = useMemo(() => styles.theme[theme]?.text, [theme]);

    const arrayChildren = Children.toArray(children);
    const amountOfSlides = arrayChildren.length;
    const showNavigationControls = showNavigation && amountOfSlides > slidesPerView;
    const showHeader = title || showNavigationControls || extraNavigationButtons;

    const { scrollToSlide } = useNavigationControls(
        sliderNode,
        itemWidth,
        arrayChildren as ReactElement[]
    );
    const carouselNavigationProps = {
        itemWidth,
        sliderNode,
        arrayChildren: arrayChildren as ReactElement[],
    };

    const carouselSliderProps = {
        gap,
    };

    const carouselItemProps = {
        h,
        height,
        itemWidth,
    };

    useIsomorphicLayoutEffect(() => {
        if (itemWidth > 0 && newActiveSlide !== undefined) {
            // Need to wait until render is done to make an update of active element and do scroll
            setTimeout(() => {
                scrollToSlide(newActiveSlide);
            }, 100);
        }
    }, [newActiveSlide, scrollToSlide, itemWidth]);

    return (
        <Box sx={{ ...styles.carousel, ...themeSurface, ...sx }} {...rest}>
            {showHeader && (
                <Flex
                    justifyContent={title ? "space-between" : "flex-end"}
                    alignItems="center"
                    px={[4, 4, 4, 6]}
                >
                    {title && (
                        <H
                            as={"h5"}
                            fontSize={["mobileH5", "tabletH5", "desktopH5"]}
                            /* In order to get the transition on the scrollbar, we need overwrite this value with important to avoid hide the headline. TBD by Designers. */
                            color="black !important"
                            aria-label={titleAriaLabel}
                            textTransform={"uppercase"}
                            {...themeText}
                        >
                            {title}
                        </H>
                    )}
                    <Flex gap={2} color="black">
                        {extraNavigationButtons}
                        {showNavigationControls && (
                            <CarouselNavigation {...carouselNavigationProps} />
                        )}
                    </Flex>
                </Flex>
            )}
            <Box w="100%">
                <CarouselSlider
                    ref={sliderRef}
                    mt={showHeader ? 3 : "unset"}
                    justifyContent={justifySlider}
                    /* The spacing on the last slide on mobile should be the same as in the gap */
                    paddingRight={[`${gap}px`, `${gap}px`, 0]}
                    {...carouselSliderProps}
                >
                    {Children.map(arrayChildren, (child) => (
                        <CarouselItem {...carouselItemProps}>{child}</CarouselItem>
                    ))}
                </CarouselSlider>
            </Box>
        </Box>
    );
};

export const CarouselSlider = forwardRef<CarouselSliderProps, "div">(
    ({ sx, gap, children, ...rest }, ref): JSX.Element => {
        return (
            <Flex sx={{ ...styles.carouselSlider, ...sx }} gap={`${gap}px`} {...rest} ref={ref}>
                {children}
            </Flex>
        );
    }
);

export const CarouselItem: FC<CarouselItemProps> = ({
    children,
    itemWidth,
    ...rest
}): JSX.Element => {
    return (
        <Flex {...rest} minW={`${itemWidth}px`}>
            {children}
        </Flex>
    );
};

export const CarouselNavigation: FC<CarouselNavigationProps> = ({
    itemWidth,
    sliderNode,
    arrayChildren,
    nextSlideTitle = "",
    previousSlideTitle = "",
    ...rest
}): JSX.Element => {
    const { isNextDisabled, isPreviousDisabled, scrollToNextSlide, scrollToPrevSlide } =
        useNavigationControls(sliderNode, itemWidth, arrayChildren);

    return (
        <Flex display={["none", "flex"]} gap={2} sx={{ ...styles.carouselNavigation }} {...rest}>
            <IconButton
                onClick={scrollToPrevSlide}
                isRound
                variant={"tertiary"}
                aria-label={previousSlideTitle}
                w={10}
                h={10}
                disabled={isPreviousDisabled}
                data-testid="carousel-previous-button"
            >
                <ChevronLeftIcon />
            </IconButton>
            <IconButton
                onClick={scrollToNextSlide}
                isRound
                variant={"tertiary"}
                aria-label={nextSlideTitle}
                w={10}
                h={10}
                disabled={isNextDisabled}
                data-testid="carousel-next-button"
            >
                <ChevronRightIcon />
            </IconButton>
        </Flex>
    );
};

export const ProductImagesPagination: FC<CarouselProductImagesPaginationProps> = ({
    slides,
    activeIndex,
    paginationsx,
}) => {
    return (
        <Flex gap={2} justify={"center"} sx={paginationsx}>
            {slides.map((_, i) => (
                <Box key={i} h="3px" w={4} bgColor={activeIndex === i ? "black" : "gray.150"}></Box>
            ))}
        </Flex>
    );
};

export const CarouselProductImageNavigation: FC<CarouselProductImageNavigationProps> = ({
    containerStyle,
    opacity,
    iconSize,
    previousSlideTitle = "",
    nextSlideTitle = "",
    onClick,
    navsx = {
        rightButton: {},
        leftButton: {},
        flexContainer: {},
    },
    swiperRef,
    playPauseTitle = "",
    setIsVideoPlaying,
    isVideoPlaying,
    playPauseControls,
    uniquePrevClass = "swiper-button-prev",
    uniqueNextClass = "swiper-button-next",
}) => {
    const { rightButton, leftButton, flexContainer } = navsx;

    const playPauseAriaLabel =
        playPauseTitle || (isVideoPlaying ? "Pause carousel" : "Play carousel");
    const handlePlayPauseClick = () => {
        if (swiperRef?.current) {
            if (isVideoPlaying) {
                swiperRef.current.autoplay.stop();
                setIsVideoPlaying(false);
            } else {
                swiperRef.current.autoplay.start();
                setIsVideoPlaying(true);
            }
        }
    };

    return (
        <Box sx={styles.iconIndicatorContainer}>
            <Box sx={styles.iconIndicatorSubContainer}>
                <Box sx={styles.iconContainer}>
                    <Flex sx={{ ...containerStyle, ...flexContainer }}>
                        <IconButton
                            className={uniquePrevClass}
                            isRound
                            variant={"standard"}
                            aria-label={previousSlideTitle}
                            icon={<ChevronLeftIcon />}
                            opacity={opacity}
                            sx={{ ...styles.iconButton, ...iconSize, ...leftButton }}
                            onClick={onClick}
                        />
                        <IconButton
                            className={uniqueNextClass}
                            isRound
                            variant={"standard"}
                            aria-label={nextSlideTitle}
                            icon={<ChevronRightIcon />}
                            opacity={opacity}
                            sx={{ ...styles.iconButton, ...iconSize, ...rightButton }}
                            onClick={onClick}
                        />
                        {playPauseControls && (
                            <IconButton
                                className="swiper-button-playpause"
                                isRound
                                variant={"standard"}
                                aria-label={playPauseAriaLabel}
                                icon={isVideoPlaying ? <PauseIcon /> : <PlayIcon />}
                                opacity={opacity}
                                sx={{ ...styles.iconButton, ...iconSize }}
                                onClick={handlePlayPauseClick}
                            />
                        )}
                    </Flex>
                </Box>
            </Box>
        </Box>
    );
};

export const CarouselProductImage: FC<CarouselHeroProps> = memo(
    ({
        children,
        timer = DEFAULT_TIMER,
        sx,
        customNavigation,
        iconSize = DEFAULT_ICON_SIZE,
        opacity = 1,
        containerStyle,
        selectedImageIndex = 0,
        nextSlideTitle = "",
        previousSlideTitle = "",
        navsx,
        hasPagination = false,
        paginationsx,
        setImageIndex,
        isVideoPlaying = false,
        setIsVideoPlaying,
        activeSlide = 0,
        setActiveSlide,
        playPauseControls = false,
        instanceId = "default",
        loop = true,
    }): JSX.Element => {
        const [activeSlideState, setActiveSlideState] = useState(
            selectedImageIndex ?? activeSlide ?? 0
        );
        const arrayChildren = Children.toArray(children).filter((child): child is ReactElement =>
            isValidElement(child)
        );

        const swiperRef = useRef<SwiperClass>(null);

        const timerClass = timer ? "swiper-pagination-timer" : "swiper-pagination";

        const uniquePrevClass = `swiper-button-prev-${instanceId}`;
        const uniqueNextClass = `swiper-button-next-${instanceId}`;

        // Only enable loop if there are more than 1 slides
        const shouldLoop = loop && arrayChildren.length > 1;

        const swiperOptions: SwiperProps = {
            tag: "div",
            speed: 450,
            slidesPerView: 1,
            spaceBetween: 24,
            initialSlide: selectedImageIndex ?? 0,

            // Performance and UX
            watchSlidesProgress: true,
            resistance: true,
            resistanceRatio: 0,
            autoHeight: false,
            cssMode: !shouldLoop && !timer,
            loop: shouldLoop,

            // Resize and observer optimizations
            updateOnWindowResize: false, // Batch resize handling
            observer: false,
            observeParents: false, // Disable mutation observers

            // Navigation controls
            navigation: {
                nextEl: `.${uniqueNextClass}`,
                prevEl: `.${uniquePrevClass}`,
                disabledClass: "swiper-button-disabled",
            },

            // Pagination (dots)
            pagination: {
                el: `.${timerClass}`,
                type: "bullets",
                clickable: true,
            },

            // Accessibility
            a11y: {
                prevSlideMessage: "Previous slide",
                nextSlideMessage: "Next slide",
            },

            // Keyboard control
            keyboard: {
                enabled: true,
                onlyInViewport: false,
            },

            // Core modules
            modules: [Navigation, Pagination, A11y, Autoplay, Keyboard],

            // Inline style
            style: {
                position: "relative",
                overflow: "clip",
            },
        };

        const swiperNavigationContainer = {
            ...styles.swiperNavigationContainer,
            ...containerStyle,
        };

        /*ts before 5.5 throws error because expression  can't be used to index type
      this however is wrong, the styles[string] is not trying to look up an array index
      but returning an object by dynamic key.
    */

        const totalSlides = arrayChildren.length;
        const currentIndex =
            (Number.isFinite(activeSlideState) ? activeSlideState : 0) ??
            swiperRef?.current?.realIndex ??
            0;
        const progressValue = totalSlides > 0 ? ((currentIndex + 1) / totalSlides) * 100 : 0;

        // Handle slides changes with proper cleanup for loop mode
        useEffect(() => {
            if (swiperRef.current) {
                const swiper = swiperRef.current;
                const currentSlidesCount = swiper.slides?.length || 0;
                const newSlidesCount = arrayChildren.length;
                if (!setActiveSlide || !setImageIndex) {
                    setActiveSlideState(0);
                }

                // If the number of slides changed significantly
                if (currentSlidesCount !== newSlidesCount) {
                    // Store current state
                    const wasAutoplayRunning = swiper.autoplay?.running;
                    const currentRealIndex = swiper.realIndex || 0;

                    // Update swiper to reflect new slides
                    swiper.update();

                    // For loop mode, we need to update the loop
                    if (shouldLoop && swiper.loopDestroy && swiper.loopCreate) {
                        swiper.loopDestroy();
                        swiper.loopCreate();
                    }

                    // Navigate to appropriate slide after update
                    setTimeout(() => {
                        if (swiperRef.current) {
                            const targetSlide = Math.min(currentRealIndex, newSlidesCount - 1);

                            if (shouldLoop && newSlidesCount > 1) {
                                swiperRef.current.slideToLoop(targetSlide, 0);
                            } else {
                                swiperRef.current.slideTo(targetSlide, 0);
                            }

                            // Restore autoplay if it was running
                            if (wasAutoplayRunning && timer && swiperRef.current.autoplay) {
                                swiperRef.current.autoplay.start();
                            }

                            // Update active slide state
                            if (setActiveSlide) {
                                setActiveSlide(targetSlide);
                            }
                        }
                    }, 0);
                }
            }
        }, [arrayChildren.length, shouldLoop, timer, setActiveSlide]);

        // Handle selected image index changes
        useEffect(() => {
            if (swiperRef.current && selectedImageIndex !== undefined) {
                const swiper = swiperRef.current;
                if (swiper.realIndex !== selectedImageIndex) {
                    if (shouldLoop) {
                        swiper.slideToLoop(selectedImageIndex);
                    } else {
                        swiper.slideTo(selectedImageIndex);
                    }
                }
                setActiveSlideState(selectedImageIndex);
            }
        }, [selectedImageIndex, shouldLoop]);

        const delay =
            arrayChildren[activeSlideState]?.props?.module?.fields?.backgroundVideo?.items[0]
                ?.duration;

        // Create a key that changes when the number of slides changes
        // This forces React to remount the Swiper component
        const swiperKey = `swiper-${instanceId}-${arrayChildren.length}`;

        return (
            <Box sx={{ ...styles.swiperContainer, ...sx }}>
                <Swiper
                    key={swiperKey}
                    onSlideChange={(swiper: SwiperClass) => {
                        const realIndex = swiper.realIndex;
                        if (setActiveSlide) {
                            setActiveSlide(realIndex);
                        }
                        // This will handle the ProductImageThumbnail to be in sync with the main image
                        if (setImageIndex) {
                            setImageIndex(realIndex);
                            setActiveSlideState(realIndex);
                        } else {
                            setActiveSlideState(realIndex);
                        }
                    }}
                    onSwiper={(swiper: SwiperClass) => ((swiperRef.current as any) = swiper)}
                    {...swiperOptions}
                    scrollbar={{ draggable: true }}
                    autoplay={
                        timer
                            ? {
                                  delay: delay ? delay * 1000 : 5000,
                                  disableOnInteraction: false,
                                  pauseOnMouseEnter: true,
                              }
                            : false
                    }
                >
                    {arrayChildren.map((child: ReactElement, index) => (
                        <SwiperSlide key={index}>{child}</SwiperSlide>
                    ))}
                    {customNavigation ?? (
                        <CarouselProductImageNavigation
                            containerStyle={swiperNavigationContainer}
                            iconSize={iconSize}
                            nextSlideTitle={nextSlideTitle}
                            onClick={(e) => e.stopPropagation()}
                            opacity={opacity}
                            previousSlideTitle={previousSlideTitle}
                            navsx={navsx}
                            swiperRef={swiperRef}
                            isVideoPlaying={isVideoPlaying}
                            setIsVideoPlaying={setIsVideoPlaying}
                            playPauseControls={playPauseControls}
                            uniquePrevClass={uniquePrevClass}
                            uniqueNextClass={uniqueNextClass}
                        />
                    )}
                </Swiper>
                {hasPagination && (
                    <ProductImagesPagination
                        paginationsx={paginationsx}
                        slides={arrayChildren}
                        activeIndex={activeSlideState}
                    />
                )}

                {!hasPagination && <CustomProgressBar value={progressValue} />}
            </Box>
        );
    }
);

CarouselProductImage.displayName = "CarouselProductImage";
